import { useCallback, useEffect, useState } from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import "@/App.css";
import { BookingContext } from "./lib/booking";
import ErrorBoundary from "./components/ErrorBoundary";
import CrisisStrip from "./components/CrisisStrip";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import BookingDrawer from "./components/BookingDrawer";
import Home from "./pages/Home";
import IndividualHealing from "./pages/IndividualHealing";
import CorporateSolutions from "./pages/CorporateSolutions";
import QuickHeals from "./pages/QuickHeals";
import Insights from "./pages/Insights";
import Store from "./pages/Store";
import Contact from "./pages/Contact";
import FAQs from "./pages/FAQs";

function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        setTimeout(() => {
          if (window.__lenis) window.__lenis.scrollTo(el, { offset: -90 });
          else el.scrollIntoView({ behavior: "smooth" });
        }, 60);
        return;
      }
    }
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function App() {
  const [booking, setBooking] = useState({ open: false, service: "" });

  const openBooking = useCallback((service = "") => {
    setBooking({ open: true, service });
  }, []);

  const closeBooking = useCallback(() => setBooking({ open: false, service: "" }), []);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <ErrorBoundary>
      <BookingContext.Provider value={{ openBooking }}>
        <div className="grain min-h-screen bg-cream text-ink">
          <BrowserRouter>
            <ScrollManager />
            <CrisisStrip />
            <Navbar />
            <main data-testid="main-content">
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/individual-healing" element={<IndividualHealing />} />
                <Route path="/corporate-solutions" element={<CorporateSolutions />} />
                <Route path="/quick-heals" element={<QuickHeals />} />
                <Route path="/insights" element={<Insights />} />
                <Route path="/store" element={<Store />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/faqs" element={<FAQs />} />
                <Route path="*" element={<Home />} />
              </Routes>
            </main>
            <Footer />
            <BookingDrawer
              open={booking.open}
              presetService={booking.service}
              onClose={closeBooking}
            />
          </BrowserRouter>
          <Toaster
            position="top-center"
            toastOptions={{
              style: {
                borderRadius: "1rem",
                background: "#1C2420",
                color: "#F9F7F2",
              },
            }}
          />
        </div>
      </BookingContext.Provider>
    </ErrorBoundary>
  );
}

export default App;
